/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useEffect, useState } from 'react';
import {
  SeriesColumnDataOptions,
  SeriesLineDataOptions,
  TooltipFormatterCallbackFunction
} from 'highcharts';
import { Chart } from '@scuf/charts';
import {
  ILegendProps,
  ITooltipProps,
  IYAxisProps
} from '@scuf/charts/dist/components/parts';
import _ from 'lodash';
import { renderToString } from 'react-dom/server';

import './StackedColumnLineChartTheme.scss';
import { IColumnProps } from '@scuf/charts/dist/components/series';
import { LINE_CHART, STACKED_BAR_CHART } from '../../Utils/Constants';
import {
  formatCurrency,
  formatTooltipHeaderUtilityBreakdown,
  formatTooltipHeaderUtilityBreakdownMonthly
} from '../../Utils/Utils';

import { Icon, Tooltip } from '@scuf/common';
import { TFunction } from '@scuf/localization/dist/types';
import { useTranslation } from '@scuf/localization';

type IAny = any;

export interface IStackedColumnDataObject {
  label: string;
  value: IAny;
  color?: string;
}

export interface IColStackLineDataObject {
  xlabel: string; // Month, just for reference
  stackTotal?: number;
  stackData: IStackedColumnDataObject[];
}

export interface ICombinedChartInfo {
  /** Array of XAxis Labels common to both Line and Column Charts*/
  commonXAxis: string[];
  lineDataTotal?: {
    lineData?: number[];
    label?: string;
  };
  /** Data for Line Charts  */
  lineData?: {
    /** Array of Line Chart Data with label for each line */
    lines: {
      label: string;
      data: { label: string; value: number | null; color?: string }[];
      color?: string;
    }[];
    linesYaxisProps?: IYAxisProps;
  };
  /** Data for Line Charts  */
  colData?: {
    data: IColStackLineDataObject[];
    additionalColProps?: IColumnProps;
    colsYAxisProps?: IYAxisProps;
  };
  /** Custom Tooltip Options and Props to Override  */
  tooltipOptions?: ITooltipProps;
  legendOptions?: ILegendProps;
  currencyCode?: string;
}

export interface IStackedColumnLineChartProps {
  colorList: string[];
  type?: string;
  chartData: ICombinedChartInfo;
  isMonthly?: boolean;
  isCostChart?: boolean;
  isHighScaled?: boolean;
  currencyCode?: string;
  isConsumptionUsageChart?: boolean;
}

const commonStackedColumnElementProps: IColumnProps = {
  pointWidth: 16,
  stacking: 'normal',
  borderWidth: 1,
  borderColor: '#363636'
};

const commonTooltipProps: ITooltipProps = {
  valueDecimals: 2,
  borderRadius: 10,
  useHTML: true,
  // backgroundColor: 'rgba(255,255,255,1)',
  padding: 0,
  borderWidth: 0

  // backgroundColor: "red",
};

const commonColumnYAxisProps: IYAxisProps = {
  allowDecimals: true,
  // min: 0,
  tickAmount: 4
};

const commonLineYAxisProps: IYAxisProps = {
  allowDecimals: true,
  // min: 0,
  tickAmount: 4
};

const commonLegendProps = (isCostChart: boolean | undefined): ILegendProps => ({
  // width:1500,
  // padding: 5,
  // position: 'center bottom',
  itemMarginTop: 0,
  itemMarginBottom: 0,
  itemStyle: {
    fontWeight: '50',
    color: 'white'
  }
});

/**
 * Component to render Stacked Columns + Lines chart
 * Can be used to render column charts only or line charts only or both together
 * Supports multiple line charts
 * default props can be overridden for all line / column chart
 */
export const StackedColumnLineChart = (props: IStackedColumnLineChartProps) => {
  const {
    chartData: {
      colData,
      tooltipOptions,
      commonXAxis,
      lineData,
      legendOptions,
      lineDataTotal
    },
    colorList,
    type,
    isHighScaled,
    currencyCode
  } = props;

  const [legendsNumber, setLegendNumber] = useState<number>(0);
  const [t] = useTranslation();

  useEffect(() => {
    let numberOfLegends = 0;
    colData?.data.forEach((d) => {
      d.stackData.forEach((c) => {
        if (isHighScaled) {
          numberOfLegends++;
        }
      });
    });

    lineData?.lines.forEach((d) => {
      d.data.forEach((c) => {
        if (isHighScaled) {
          numberOfLegends++;
        }
      });
    });

    setLegendNumber(numberOfLegends);
  }, []);

  const prepareChartElements = () => {
    const columnMap: {
      [subCat: string]: {
        data: SeriesColumnDataOptions[];
        color?: string;
        type?: string;
      };
    } = {};

    const LineMap: {
      [subCat: string]: {
        data: SeriesColumnDataOptions[];
        color?: string;
        type?: string;
      };
    } = {};

    // prepare ds
    colData?.data.forEach((d) => {
      d.stackData.forEach((c) => {
        columnMap[c.label] = { data: [] };
      });
    });

    // populate data
    let numberOfLegends = 0;
    colData?.data.forEach((d) => {
      d.stackData.forEach((c) => {
        if (isHighScaled) {
          numberOfLegends++;
        }
        columnMap[c.label].data = [
          ...columnMap[c.label].data,
          { y: c.value, color: c.color }
        ];
      });
    });

    if (lineData) {
      lineData?.lines.forEach((d) => {
        d.data.forEach((c) => {
          LineMap[c.label] = { data: [] };
        });
      });

      lineData?.lines.forEach((d) => {
        d.data.forEach((c) => {
          if (isHighScaled) {
            numberOfLegends++;
          }
          LineMap[c.label].data = [
            ...LineMap[c.label].data,
            { y: c.value, color: c.color }
          ];
        });
      });
    }

    let _colorIndex = 0;
    const columnStacks = _.map(columnMap, (c, key) => {
      const element = (
        <Chart.Column
          // color={colorList[_colorIndex % colorList.length]}
          // colorIndex={_colorIndex % colorList.length}
          key={`${key}`}
          name={`${key}`}
          data={c.data}
          yAxis={'stack_axis'}
          color={c.color}
          {...commonStackedColumnElementProps}
          {...colData?.additionalColProps}
        />
      );
      // }

      _colorIndex += 1;
      return {
        element
      };
    });

    const totalLine = lineDataTotal && (
      <Chart.Line
        yAxis={'line_axis'}
        key={lineDataTotal.label}
        name={lineDataTotal.label}
        data={lineDataTotal.lineData}
        color={'#989898'}
        legendIndex={0}
        marker={{
          symbol: 'circle',
          // lineColor: type === STACKED_BAR_CHART ? '#000000' : '',
          lineColor: '#303030',
          lineWidth: 1,
          radius: 3
        }}
      />
    );

    const lines = _.map(LineMap, (c, key) => {
      let element;
      element = (
        <Chart.Line
          yAxis={'line_axis'}
          key={`${key}`}
          name={`${key}`}
          data={c.data}
          color={c.color}
          legendIndex={1}
          marker={{
            symbol: 'circle',
            // lineColor: type === STACKED_BAR_CHART ? '#000000' : '',
            lineColor: '#363636',
            lineWidth: 1,
            radius: 3
          }}
        />
      );
      _colorIndex += 1;
      return {
        element
      };
    });

    return {
      columnStacks,
      lines: lines || [],
      totalLine
    };
  };

  const { columnStacks, lines, totalLine } = prepareChartElements();
  const xAxis = commonXAxis;

  const formatDateStyle = (value: IAny) => {
    // const classNameToApply = props.isCostChart
    //   ? 'utility-xaxis-labels-for-cost'
    //   : 'utility-xaxis-labels';

    if (props.isMonthly) {
      return `<div >
      ${value && value.toString().split(',')[0]}
      </div>`;
    } else {
      return `<div>${value &&
        value.toString().split('-')[1] &&
        ((value
          .toString()
          .split('-')[0]
          .split(',')[0] +
          '-' +
          value
            .toString()
            .split('-')[1]
            .split(',')[0]) as string)}</div>`;
    }
  };

  return (
    <div className="stacked-column-line-chart-wrapper">
      <Chart
        height={isHighScaled ? (legendsNumber > 8 ? 400 : 300) : 250}
        showLegend={false}
        colors={type === STACKED_BAR_CHART ? colorList : undefined}
      >
        {columnStacks.map((c) => c.element)}
        {lines.map((l) => l.element)}
        {totalLine}
        <Chart.YAxis
          className="column-chart-y-axis"
          id={'stack_axis'}
          {...commonColumnYAxisProps}
          {...colData?.colsYAxisProps}
        />
        <Chart.YAxis
          className="line-chart-y-axis"
          id={'line_axis'}
          {...commonLineYAxisProps}
          {...lineData?.linesYaxisProps}
          labels={
            props.isCostChart || props.isConsumptionUsageChart
              ? {
                  formatter: function() {
                    return formatCurrency(this.value, currencyCode, false);
                  }
                }
              : {}
          }
        />
        <Chart.XAxis
          className={'x-axis-labels'}
          id={'common_xaxis'}
          categories={xAxis}
          labels={{
            rotation: !props.isMonthly ? -25 : 0,
            useHTML: true,
            formatter: function(this) {
              return formatDateStyle(this.value);
            }
          }}
        />
        <Chart.Tooltip {...commonTooltipProps} shared {...tooltipOptions} />
        <Chart.Legend
          {...commonLegendProps(props.isCostChart)}
          {...legendOptions}
          useHTML={true}
          renderLabel={(details) => {
            return <div className="legend-custom-name">{t(details.name)}</div>;
          }}
        />
      </Chart>
    </div>
  );
};

export const stackedColumnLineTooltipFormatter = (
  tooltipTitle: string,
  tooltipConfig: {
    numDecimals: number;
    dataUnit?: string;
    total?: boolean;
    percentages?: boolean;
    chartData?: ICombinedChartInfo;
    pointFormatter?: (value: number) => string;
    type?: string;
    isMonthly: boolean | undefined;
  },
  t: TFunction
): TooltipFormatterCallbackFunction => {
  const {
    dataUnit,
    total,
    percentages,
    numDecimals,
    pointFormatter,
    type,
    isMonthly
  } = tooltipConfig;

  const renderTotalRow = (points: IAny[]) => {
    const totalValue = points[0].total || _.sum(points.map((p: IAny) => p.y));
    return (
      <tr className="tooltip-footer">
        <td></td>
        <td>Total</td>
        <td>
          {pointFormatter
            ? pointFormatter(totalValue)
            : totalValue.toFixed(numDecimals || 1)}
        </td>
        <td>{dataUnit ? dataUnit : ''}</td>
        {percentages ? <td></td> : <></>}
      </tr>
    );
  };

  return function(this: IAny, tooltip?: IAny): string {
    const formattedTooltipDate = isMonthly
      ? formatTooltipHeaderUtilityBreakdownMonthly(this)
      : formatTooltipHeaderUtilityBreakdown(this);

    return renderToString(
      <div className="stacked-column-line-chart-tooltip-wrapper combined-tooltip-wrapper">
        <div className="tooltip-header">
          <span>{`${tooltipTitle} - ${formattedTooltipDate}`}</span>
        </div>
        <div>
          <table>
            {this.points.map((p: IAny, i: number) => {
              return (
                <tr key={`${p.series.name}-${i}`}>
                  <td>
                    <span
                      className="highcharts-tooltip-label-marker"
                      style={{
                        color: p.color
                      }}
                    >
                      {p.series.type.toLowerCase() === LINE_CHART.toLowerCase()
                        ? '\u25AC'
                        : '\u25AE'}
                      {/* {type && type === LINE_CHART ? '\u25AC' : '\u25AE'} */}
                    </span>
                  </td>
                  <td
                    className={
                      p.series.name.toLowerCase() === 'total rate'
                        ? 'plot-label tooltip-footer-total'
                        : 'plot-label'
                    }
                  >
                    {t(p.series.name)}
                  </td>
                  <td
                    className={
                      p.series.name.toLowerCase() === 'total rate'
                        ? 'tooltip-footer-total'
                        : ''
                    }
                  >
                    {pointFormatter
                      ? pointFormatter(p.y)
                      : p.y.toFixed(numDecimals || 1)}
                  </td>
                  <td
                    className={
                      p.series.name.toLowerCase() === 'total rate'
                        ? 'tooltip-footer-total'
                        : ''
                    }
                  >
                    {dataUnit ? dataUnit : ''}
                  </td>
                  {/* {percentages ? (
                    <td>{`(${p.percentage.toFixed(numDecimals || 1)}%)`}</td>
                  ) : (
                    <></>
                  )} */}
                </tr>
              );
            })}
            {total ? renderTotalRow(this.points) : <></>}
          </table>
        </div>
      </div>
    );
  };
};

export const consumptionUsageTooltipFormatter = (
  currencyCode: string,
  tooltipTitle: string,
  tooltipConfig: {
    numDecimals: number;
    dataUnit?: string;
    usageUnit?: string;
    total?: boolean;
    percentages?: boolean;
    chartData?: ICombinedChartInfo;
    pointFormatter?: (value: number) => string;
    type?: string;
    isMonthly: boolean | undefined;
    monthDate?: string;
  },
  t: TFunction,
  conspTotalUsageMonthwise?: IAny
): TooltipFormatterCallbackFunction => {
  const {
    dataUnit,
    usageUnit,
    total,
    percentages,
    numDecimals,
    pointFormatter,
    type,
    isMonthly
  } = tooltipConfig;

  const renderTotalRow = (
    points: IAny[],
    unit?: string,
    consumptionCost?: boolean,
    currencyCode?: string
  ) => {
    const filteredtotal =
      points &&
      points.length > 0 &&
      conspTotalUsageMonthwise.filter(
        (cnsp: IAny) => cnsp.date === points[0].x
      );
    const Unit =
      !consumptionCost &&
      filteredtotal &&
      filteredtotal.length > 0 &&
      filteredtotal[0].unit;
    const totalValue =
      consumptionCost && points && points.length > 0
        ? points[0].total || _.sum(points.map((p: IAny) => p.y))
        : filteredtotal &&
          filteredtotal.length > 0 &&
          !isNaN(filteredtotal[0].total)
        ? filteredtotal[0].total
        : 0;
    return (
      <tr className="tooltip-footer">
        <td></td>
        <td>Total</td>
        {/* {consumptionCost && <td>{unit ? unit : ''}</td>} */}
        <td>
          {consumptionCost
            ? formatCurrency(
                pointFormatter
                  ? pointFormatter(totalValue)
                  : totalValue.toFixed(numDecimals || 1),
                currencyCode
              )
            : pointFormatter
            ? pointFormatter(totalValue)
            : totalValue.toFixed(numDecimals || 1)}
        </td>
        {!consumptionCost && <td>{Unit ? Unit : ''}</td>}
        {percentages ? <td></td> : <></>}
      </tr>
    );
  };

  return function(this: IAny, tooltip?: IAny): string {
    const formattedTooltipDate = isMonthly
      ? formatTooltipHeaderUtilityBreakdownMonthly(this)
      : formatTooltipHeaderUtilityBreakdown(this);

    return renderToString(
      <div className="stacked-column-line-chart-tooltip-wrapper combined-tooltip-wrapper">
        <div className="tooltip-header">
          <span>{`${tooltipTitle} - ${formattedTooltipDate}`}</span>
        </div>
        <div>
          <div className="Utility_Consumption_ToolTip">
            <table>
              {this.points
                .filter(
                  (p: IAny) =>
                    p.series.type.toLowerCase() === LINE_CHART.toLowerCase()
                )
                .map((p: IAny, i: number) => {
                  return (
                    <tr key={`${p.series.name}-${i}`}>
                      <td>
                        <span
                          className="highcharts-tooltip-label-marker"
                          style={{
                            color: p.color
                          }}
                        >
                          {p.series.type.toLowerCase() ===
                          LINE_CHART.toLowerCase()
                            ? '\u25AC'
                            : '\u25AE'}
                          {/* {type && type === LINE_CHART ? '\u25AC' : '\u25AE'} */}
                        </span>
                      </td>
                      <td className="plot-label">{t(p.series.name)}</td>
                      {/* <td>{dataUnit ? dataUnit : ''}</td> */}
                      <td>
                        {formatCurrency(
                          pointFormatter
                            ? pointFormatter(p.y)
                            : p.y.toFixed(numDecimals || 1),
                          currencyCode,
                          false
                        )}
                      </td>

                      {/* {percentages ? (
                        <td>{`(${p.percentage.toFixed(numDecimals || 1)}%)`}</td>
                      ) : (
                        <></>
                      )} */}
                    </tr>
                  );
                })}
              {total ? (
                renderTotalRow(
                  this.points.filter(
                    (p: IAny) =>
                      p.series.type.toLowerCase() === LINE_CHART.toLowerCase()
                  ),
                  dataUnit,
                  true,
                  currencyCode
                )
              ) : (
                <></>
              )}
            </table>
            <table>
              {this.points
                .filter(
                  (p: IAny) =>
                    p.series.type.toLowerCase() !== LINE_CHART.toLowerCase()
                )
                .map((p: IAny, i: number) => {
                  return (
                    <tr key={`${p.series.name}-${i}`}>
                      <td>
                        <span
                          className="highcharts-tooltip-label-marker"
                          style={{
                            color: p.color
                          }}
                        >
                          {p.series.type.toLowerCase() ===
                          LINE_CHART.toLowerCase()
                            ? '\u25AC'
                            : '\u25AE'}
                        </span>
                      </td>
                      <td className="plot-label">{p.series.name}</td>
                      <td>
                        {pointFormatter
                          ? pointFormatter(p.y)
                          : p.y.toFixed(numDecimals || 1)}
                      </td>
                      <td>{usageUnit ? usageUnit : ''}</td>
                      {/* {percentages ? (
                        <td>{`(${p.percentage.toFixed(numDecimals || 1)}%)`}</td>
                      ) : (
                        <></>
                      )} */}
                    </tr>
                  );
                })}
              {total ? (
                renderTotalRow(
                  this.points.filter(
                    (p: IAny) =>
                      p.series.type.toLowerCase() !== LINE_CHART.toLowerCase()
                  ),
                  usageUnit
                )
              ) : (
                <></>
              )}
            </table>
          </div>
        </div>
      </div>
    );
  };
};

export const electricityCostLineTooltipFormatter = (
  currencyCode: string,
  tooltipTitle: string,
  tooltipConfig: {
    numDecimals: number;
    dataUnit?: string;
    total?: boolean;
    percentages?: boolean;
    chartData?: ICombinedChartInfo;
    pointFormatter?: (value: number) => string;
    type?: string;
    isMonthly: boolean | undefined;
  },
  t: TFunction
): TooltipFormatterCallbackFunction => {
  const {
    dataUnit,
    total,
    percentages,
    numDecimals,
    pointFormatter,
    type,
    isMonthly
  } = tooltipConfig;

  const renderTotalRow = (points: IAny[]) => {
    const totalValue = points[0].total || _.sum(points.map((p: IAny) => p.y));
    return (
      <tr className="tooltip-footer">
        <td></td>
        <td>Total</td>
        <td>
          {pointFormatter
            ? pointFormatter(totalValue)
            : totalValue.toFixed(numDecimals || 1)}
        </td>
        <td>{dataUnit ? dataUnit : ''}</td>
        {percentages ? <td></td> : <></>}
      </tr>
    );
  };

  return function(this: IAny, tooltip?: IAny): string {
    const formattedTooltipDate = isMonthly
      ? formatTooltipHeaderUtilityBreakdownMonthly(this)
      : formatTooltipHeaderUtilityBreakdown(this);

    return renderToString(
      <div className="stacked-column-line-chart-tooltip-wrapper combined-tooltip-wrapper">
        <div className="tooltip-header">
          <span>{`${tooltipTitle} - ${formattedTooltipDate}`}</span>
        </div>
        <div>
          <table>
            {this.points.map((p: IAny, i: number) => {
              return (
                <tr key={`${p.series.name}-${i}`}>
                  <td>
                    <span
                      className="highcharts-tooltip-label-marker"
                      style={{
                        color: p.color
                      }}
                    >
                      {p.series.type.toLowerCase() === LINE_CHART.toLowerCase()
                        ? '\u25AC'
                        : '\u25AE'}
                      {/* {type && type === LINE_CHART ? '\u25AC' : '\u25AE'} */}
                    </span>
                  </td>
                  <td
                    className={
                      p.series.name.toLowerCase() === 'total cost'
                        ? 'plot-label tooltip-footer-total'
                        : 'plot-label'
                    }
                  >
                    {t(p.series.name)}
                  </td>
                  <td
                    className={
                      p.series.name.toLowerCase() === 'total cost'
                        ? 'tooltip-footer-total'
                        : ''
                    }
                  >
                    {/* {dataUnit ? dataUnit : ''} */}
                  </td>
                  <td
                    className={
                      p.series.name.toLowerCase() === 'total cost'
                        ? 'tooltip-footer-total'
                        : ''
                    }
                  >
                    {formatCurrency(
                      pointFormatter
                        ? pointFormatter(p.y)
                        : p.y.toFixed(numDecimals || 1),
                      currencyCode,
                      false
                    )}
                  </td>
                  {/* {percentages ? (
                    <td>{`(${p.percentage.toFixed(numDecimals || 1)}%)`}</td>
                  ) : (
                    <></>
                  )} */}
                </tr>
              );
            })}
            {/* {total ? renderTotalRow(this.points) : <></>} */}
          </table>
        </div>
      </div>
    );
  };
};
